export enum CONTENT_STATE {
  draft = 0,
  published = 1,
}

export function getContentStateLabel(state: CONTENT_STATE): string {
  switch (state) {
    case CONTENT_STATE.draft:
      return 'Draft';
    case CONTENT_STATE.published:
      return 'Published';
    default:
      return '';
  }
}


export function getReviewStateLabel(reviewState: boolean): string {
  switch (reviewState) {
    case true:
      return 'Reviewed';
    case false:
      return 'Unreviewed';
    default:
      return '';
  }
}

/** Label for story list filter chips / UI (`null` = not filtering by reviewed). */
export function getStoryReviewFilterLabel(reviewed: boolean | null): string {
  if (reviewed === null) {
    return 'Both';
  }
  return getReviewStateLabel(reviewed);
}

export const STORY_STATE_OPTIONS = [CONTENT_STATE.draft, CONTENT_STATE.published];

// story priority is used to help determine what story we should prioritize to our users
export const STORY_PRIORITY_OPTIONS = [
  { label: 'High', value: 1 },
  { label: 'Medium', value: 2 },
  { label: 'Low', value: 3 },
];

export enum CONTENT_PROCESSING_STATUS {
  Pending = 0,
  Processing = 1,
  Completed = 2,
  Failed = 3,
}

export function getContentProcessingStatusLabel(status: CONTENT_PROCESSING_STATUS): string {
  switch (status) {
    case CONTENT_PROCESSING_STATUS.Pending:
      return 'Pending';
    case CONTENT_PROCESSING_STATUS.Processing:
      return 'Processing';
    case CONTENT_PROCESSING_STATUS.Completed:
      return 'Completed';
    case CONTENT_PROCESSING_STATUS.Failed:
      return 'Failed';
    default:
      return 'Unknown';
  }
}

export type MediaType = {
  label: string;
  value: string;
  icon: string;
};

export const EVENT_MEDIA_TYPES = {
  text: {
    label: 'Text & Image',
    value: 'funFact',
    icon: 'mdi:text',
  },
  video: {
    label: 'Video',
    value: 'video',
    icon: 'mdi:video',
  },
  audio: {
    label: 'Audio',
    value: 'audio',
    icon: 'mdi:audio',
  },
};

// defines the different types of stories we currently support
export type StoryMediaType = {
  label: string;
  value: string;
  icon: string;
  cropAspectRatio?: number;
};
export const STORY_MEDIA_TYPES: Record<string, StoryMediaType> = {
  funFact: {
    label: 'Text',
    value: 'funFact',
    icon: 'mdi:text',
    cropAspectRatio: 9 / 16,
  },
  trivia: {
    label: 'Trivia',
    value: 'trivia',
    icon: 'mdi:puzzle',
    cropAspectRatio: 9 / 16,
  },
  spotlight: {
    label: 'Spotlight',
    value: 'spotlight',
    icon: 'custom:spotlight',
  },
  audio: {
    label: 'Audio',
    value: 'audio',
    icon: 'mdi:audio',
    cropAspectRatio: 9 / 16,
  },
  video: {
    label: 'Video',
    value: 'video',
    icon: 'mdi:video',
    cropAspectRatio: 9 / 16,
  },
};
export type StoryMediaTypeKeys = keyof typeof STORY_MEDIA_TYPES;

export const getStoryCropAspectRatio = (mediaType: StoryMediaTypeKeys): number | undefined => {
  const storyMedia = STORY_MEDIA_TYPES[mediaType];
  if (!storyMedia) {
    return undefined;
  }
  return storyMedia.cropAspectRatio;
}

// toArray helper function
export function toMediaTypeArray(mediaTypes: Record<string, MediaType>): MediaType[] {
  return Object.values(mediaTypes).filter(
    (item): item is MediaType => typeof item === 'object' && item !== null
  );
}

export const defaultImage =
  'https://amuse-bucket.nyc3.cdn.digitaloceanspaces.com/default/default-thumb.png';

export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_TIME_FORMAT = 'YYYY-MM-DD h:mm A';
export const DATE_TIME_TZ_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';

export const UUID_NIL = '00000000-0000-0000-0000-000000000000';

// ----------------------------------------------------------------------
// ATLAS SEARCH MODE
// ----------------------------------------------------------------------

export enum ATLAS_SEARCH_MODE {
  CITATIONS_AND_WEBSEARCH = 0, // default, combine citations with web search
  CITATIONS = 1, // only citations, no web search
  WEBSEARCH = 2, // only web search, no citations
}

export function getAtlasSearchModeLabel(mode: ATLAS_SEARCH_MODE): string {
  switch (mode) {
    case ATLAS_SEARCH_MODE.CITATIONS_AND_WEBSEARCH:
      return 'Place sources and web search';
    case ATLAS_SEARCH_MODE.CITATIONS:
      return 'Place sources only';
    case ATLAS_SEARCH_MODE.WEBSEARCH:
      return 'Web search only';
    default:
      return '';
  }
}

export const ATLAS_SEARCH_MODE_OPTIONS = [
  { label: 'Place sources and web search', value: ATLAS_SEARCH_MODE.CITATIONS_AND_WEBSEARCH },
  { label: 'Place sources only', value: ATLAS_SEARCH_MODE.CITATIONS },
  { label: 'Web search only', value: ATLAS_SEARCH_MODE.WEBSEARCH },
];
